import type { MouseEventHandler, ReactNode } from "react";

import { X as CloseX } from "@untitledui/icons";

import { Dot } from "@/assets/icons/dot-icon";
import { cx } from "@/utils/cx";

import type {
	BadgeColors,
	BadgeTypes,
	BadgeTypeToColorMap,
	FlagTypes,
	IconComponentType,
	Sizes,
} from "./badge-types";

import { filledColors } from "./badge-styles";
import { badgeTypes } from "./badge-types";

const addonOnlyColors = Object.fromEntries(
	Object.entries(filledColors).map(([key, value]) => [
		key,
		{ addon: value.addon, root: "" },
	]),
) as Record<BadgeColors, { addon: string; root: string }>;

const withPillTypes = {
	[badgeTypes.badgeColor]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset",
		styles: filledColors,
	},
	[badgeTypes.badgeModern]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset shadow-xs",
		styles: {
			gray: {
				addon: "text-gray-500",
				addonButton:
					"hover:bg-utility-neutral-100 text-utility-neutral-400 hover:text-utility-neutral-500",
				root: "bg-primary text-secondary ring-primary",
			},
		},
	},
	[badgeTypes.pillColor]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset",
		styles: filledColors,
	},
};

const withBadgeTypes = {
	[badgeTypes.badgeColor]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset",
		styles: filledColors,
	},
	[badgeTypes.badgeModern]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset bg-primary text-secondary ring-primary shadow-xs",
		styles: addonOnlyColors,
	},
	[badgeTypes.pillColor]: {
		common:
			"size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset",
		styles: filledColors,
	},
};

export type BadgeColor<T extends BadgeTypes> = BadgeTypeToColorMap<
	typeof withPillTypes
>[T];

interface BadgeProps<T extends BadgeTypes> {
	children: ReactNode;
	className?: string;
	color?: BadgeColor<T>;
	disabled?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	size?: Sizes;
	type?: T;
}

export const Badge = <T extends BadgeTypes>(props: BadgeProps<T>) => {
	const {
		children,
		color = "gray",
		disabled,
		onClick,
		size = "md",
		type = "pill-color",
	} = props;
	const colors = withPillTypes[type];

	const pillSizes = {
		lg: "py-1 px-3 text-sm font-medium",
		md: "py-0.5 px-2.5 text-sm font-medium",
		sm: "py-0.5 px-2 text-xs font-medium",
	};
	const badgeSizes = {
		lg: "py-1 px-2.5 text-sm font-medium rounded-lg",
		md: "py-0.5 px-2 text-sm font-medium",
		sm: "py-0.5 px-1.5 text-xs font-medium",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return onClick ? (
		<button
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
				"cursor-pointer border-none transition duration-100 ease-linear hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50",
				props.className,
			)}
			disabled={disabled}
			onClick={onClick}
			type="button"
		>
			{children}
		</button>
	) : (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
				props.className,
			)}
		>
			{children}
		</span>
	);
};
interface BadgeWithDotProps<T extends BadgeTypes> {
	children: ReactNode;
	className?: string;
	color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
	size?: Sizes;
	type?: T;
}

export const BadgeWithDot = <T extends BadgeTypes>(
	props: BadgeWithDotProps<T>,
) => {
	const {
		children,
		className,
		color = "gray",
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withBadgeTypes[type];

	const pillSizes = {
		lg: "gap-1.5 py-1 pl-2.5 pr-3 text-sm font-medium",
		md: "gap-1.5 py-0.5 pl-2 pr-2.5 text-sm font-medium",
		sm: "gap-1 py-0.5 pl-1.5 pr-2 text-xs font-medium",
	};

	const badgeSizes = {
		lg: "gap-1.5 py-1 px-2.5 text-sm font-medium rounded-lg",
		md: "gap-1.5 py-0.5 px-2 text-sm font-medium",
		sm: "gap-1 py-0.5 px-1.5 text-xs font-medium",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
				className,
			)}
		>
			<Dot className={colors.styles[color].addon} size="sm" />
			{children}
		</span>
	);
};

interface BadgeWithIconProps<T extends BadgeTypes> {
	children: ReactNode;
	className?: string;
	color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T];
	"data-testid"?: string;
	iconLeading?: IconComponentType;
	iconTrailing?: IconComponentType;
	size?: Sizes;
	type?: T;
}

export const BadgeWithIcon = <T extends BadgeTypes>(
	props: BadgeWithIconProps<T>,
) => {
	const {
		children,
		className,
		color = "gray",
		"data-testid": dataTestId,
		iconLeading: IconLeading,
		iconTrailing: IconTrailing,
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withBadgeTypes[type];

	const icon = IconLeading ? "leading" : "trailing";

	const pillSizes = {
		lg: {
			leading: "gap-1 py-1 pr-3 pl-2.5 text-sm font-medium",
			trailing: "gap-1 py-1 pl-3 pr-2.5 text-sm font-medium",
		},
		md: {
			leading: "gap-1 py-0.5 pr-2.5 pl-2 text-sm font-medium",
			trailing: "gap-1 py-0.5 pl-2.5 pr-2 text-sm font-medium",
		},
		sm: {
			leading: "gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-medium",
			trailing: "gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-medium",
		},
	};
	const badgeSizes = {
		lg: {
			leading: "gap-1 py-1 pr-2.5 pl-2 text-sm font-medium rounded-lg",
			trailing: "gap-1 py-1 pl-2.5 pr-2 text-sm font-medium rounded-lg",
		},
		md: {
			leading: "gap-1 py-0.5 pr-2 pl-1.5 text-sm font-medium",
			trailing: "gap-1 py-0.5 pl-2 pr-1.5 text-sm font-medium",
		},
		sm: {
			leading: "gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-medium",
			trailing: "gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-medium",
		},
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size][icon],
				colors.styles[color].root,
				className,
			)}
			data-testid={dataTestId}
		>
			{IconLeading && (
				<IconLeading
					className={cx(colors.styles[color].addon, "size-3 stroke-3")}
				/>
			)}
			{children}
			{IconTrailing && (
				<IconTrailing
					className={cx(colors.styles[color].addon, "size-3 stroke-3")}
				/>
			)}
		</span>
	);
};

interface BadgeWithFlagProps<T extends BadgeTypes> {
	children: ReactNode;
	color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
	flag?: FlagTypes;
	size?: Sizes;
	type?: T;
}

export const BadgeWithFlag = <T extends BadgeTypes>(
	props: BadgeWithFlagProps<T>,
) => {
	const {
		children,
		color = "gray",
		flag = "AU",
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withPillTypes[type];

	const pillSizes = {
		lg: "gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium",
		md: "gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium",
		sm: "gap-1 py-0.5 pl-0.75 pr-2 text-xs font-medium",
	};
	const badgeSizes = {
		lg: "gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg",
		md: "gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium",
		sm: "gap-1 py-0.5 pl-1 pr-1.5 text-xs font-medium",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
			)}
		>
			<img
				alt={`${flag} flag`}
				className="size-4 max-w-none rounded-full"
				src={`https://www.untitledui.com/images/flags/${flag}.svg`}
			/>
			{children}
		</span>
	);
};

interface BadgeWithImageProps<T extends BadgeTypes> {
	children: ReactNode;
	color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
	imgSrc: string;
	size?: Sizes;
	type?: T;
}

export const BadgeWithImage = <T extends BadgeTypes>(
	props: BadgeWithImageProps<T>,
) => {
	const {
		children,
		color = "gray",
		imgSrc,
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withPillTypes[type];

	const pillSizes = {
		lg: "gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium",
		md: "gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium",
		sm: "gap-1 py-0.5 pl-0.75 pr-2 text-xs font-medium",
	};
	const badgeSizes = {
		lg: "gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg",
		md: "gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium",
		sm: "gap-1 py-0.5 pl-1 pr-1.5 text-xs font-medium",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
			)}
		>
			<img
				alt="Badge image"
				className="size-4 max-w-none rounded-full"
				src={imgSrc}
			/>
			{children}
		</span>
	);
};

interface BadgeWithButtonProps<T extends BadgeTypes> {
	/**
	 * The label for the button.
	 */
	buttonLabel?: string;
	children: ReactNode;
	color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
	icon?: IconComponentType;
	/**
	 * The click event handler for the button.
	 */
	onButtonClick?: MouseEventHandler<HTMLButtonElement>;
	size?: Sizes;
	type?: T;
}

export const BadgeWithButton = <T extends BadgeTypes>(
	props: BadgeWithButtonProps<T>,
) => {
	const {
		buttonLabel,
		children,
		color = "gray",
		icon: Icon = CloseX,
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withPillTypes[type];

	const pillSizes = {
		lg: "gap-0.5 py-1 pl-3 pr-1.5 text-sm font-medium",
		md: "gap-0.5 py-0.5 pl-2.5 pr-1 text-sm font-medium",
		sm: "gap-0.5 py-0.5 pl-2 pr-0.75 text-xs font-medium",
	};
	const badgeSizes = {
		lg: "gap-0.5 py-1 pl-2.5 pr-1.5 text-sm font-medium rounded-lg",
		md: "gap-0.5 py-0.5 pl-2 pr-1 text-sm font-medium",
		sm: "gap-0.5 py-0.5 pl-1.5 pr-0.75 text-xs font-medium",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
			)}
		>
			{children}
			<button
				aria-label={buttonLabel}
				className={cx(
					"flex cursor-pointer items-center justify-center p-0.5 outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2",
					colors.styles[color].addonButton,
					type === "pill-color" ? "rounded-full" : "rounded-[3px]",
				)}
				onClick={props.onButtonClick}
				type="button"
			>
				<Icon className="size-3 stroke-[3px] transition-inherit-all" />
			</button>
		</span>
	);
};

interface BadgeIconProps<T extends BadgeTypes> {
	children?: ReactNode;
	color?: BadgeTypeToColorMap<typeof withPillTypes>[T];
	icon: IconComponentType;
	size?: Sizes;
	type?: T;
}

export const BadgeIcon = <T extends BadgeTypes>(props: BadgeIconProps<T>) => {
	const {
		color = "gray",
		icon: Icon,
		size = "md",
		type = "pill-color",
	} = props;

	const colors = withPillTypes[type];

	const pillSizes = {
		lg: "p-2",
		md: "p-1.5",
		sm: "p-1.25",
	};

	const badgeSizes = {
		lg: "p-2 rounded-lg",
		md: "p-1.5",
		sm: "p-1.25",
	};

	const sizes = {
		[badgeTypes.badgeColor]: badgeSizes,
		[badgeTypes.badgeModern]: badgeSizes,
		[badgeTypes.pillColor]: pillSizes,
	};

	return (
		<span
			className={cx(
				colors.common,
				sizes[type][size],
				colors.styles[color].root,
			)}
		>
			<Icon className={cx("size-3 stroke-[3px]", colors.styles[color].addon)} />
		</span>
	);
};
